import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../ui/accordion';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Alert, AlertDescription } from '../ui/alert';
import {
  AlertCircle, AlertTriangle, Car, BatteryCharging, Play, Loader2, RotateCcw, CarFront, Cog, Route, Fuel, Settings2,
  SlidersHorizontal, Zap,
} from 'lucide-react';
import {
  VEHICLE_FIELD_SPECS, GEARBOX_FIELD_SPECS, GEAR_RATIO_SPEC, ENGINE_FIELD_SPECS, MOTOR_FIELD_SPECS, BATTERY_FIELD_SPECS,
  RULE_FIELD_SPECS, ECMS_S_SPEC, DRIVE_CYCLE_OPTIONS, SAMPLING_OPTIONS, ARCHITECTURE_OPTIONS, ENGINE_MAP_OPTIONS,
  ECMS_MODE_OPTIONS, DRIVE_TECHNOLOGY_OPTIONS, DEFAULT_RULE, DEFAULT_ECMS, HEV_PRESETS, DEFAULT_PRESET_ID, findPreset,
  validateField, toRawStrings, gearboxToRaw, parseGearRatios, validateCrossFields,
} from '../../lib/hevEnergyTools';
import { simulationApi } from '../../lib/simulationApi';
import { useChartZoom } from './simulator/useChartZoom';
import { HevSummaryPanel, HevTimeTraces, HevModeBand, HevEnergyBreakdown, useTraceRows } from './simulator/HevCharts';
import HevArchitectureDiagram from './simulator/HevArchitectureDiagram';

// Plotly (contour map) is large: load it only once results are on screen.
const HevBsfcMapChart = lazy(() => import('./simulator/HevBsfcMapChart'));

const FieldInput = ({ spec, raw, error, onChange, testPrefix, placeholder, type = 'number' }) => (
  <div className="space-y-1 min-w-0">
    <Label htmlFor={`${testPrefix}-${spec.key}`} className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 leading-tight">
      {spec.label} <span className="normal-case text-muted-foreground/70">({spec.unit})</span>
    </Label>
    <Input
      id={`${testPrefix}-${spec.key}`} type={type} step="any" value={raw ?? ''} placeholder={placeholder} inputMode="decimal"
      onChange={(e) => onChange(spec.key, e.target.value)} className="font-mono h-8 text-sm"
      aria-invalid={!!error} aria-describedby={error ? `${testPrefix}-${spec.key}-error` : undefined}
      data-testid={`${testPrefix}-${spec.key}-input`}
    />
    {error && (
      <p id={`${testPrefix}-${spec.key}-error`} className="flex items-center gap-1 text-xs text-destructive" data-testid={`${testPrefix}-${spec.key}-error`}>
        <AlertCircle className="h-3 w-3 shrink-0" /> {error}
      </p>
    )}
  </div>
);

const SelectField = ({ label, value, onChange, options, testId }) => (
  <div className="space-y-1 min-w-0">
    <Label className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground leading-tight">{label}</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-8 text-sm" data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>
        {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

const Section = ({ value, icon: Icon, title, children }) => (
  <AccordionItem value={value}>
    <AccordionTrigger className="font-chivo text-xs font-bold uppercase tracking-wider text-muted-foreground py-2">
      <span className="flex items-center gap-2"><Icon className="h-4 w-4" /> {title}</span>
    </AccordionTrigger>
    <AccordionContent className="space-y-3 pb-3 pt-0">{children}</AccordionContent>
  </AccordionItem>
);

const GRID = 'grid sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3';

function validateGroup(raw, specs) {
  const errors = {};
  const values = {};
  for (const spec of specs) {
    const err = validateField(spec, raw[spec.key]);
    if (err) errors[spec.key] = err;
    else if (raw[spec.key] === '' && spec.optional) values[spec.key] = null;
    else values[spec.key] = Number(raw[spec.key]);
  }
  return { errors, values };
}

// Hover/scrub position lives outside React state of the big component, so moving the mouse
// over a trace only re-renders the diagram and mode band, not every chart.
function useScrubStore() {
  const ref = useRef(null);
  if (!ref.current) {
    const listeners = new Set();
    let value = 0;
    ref.current = {
      get: () => value,
      set: (v) => { if (v !== value) { value = v; listeners.forEach((l) => l(v)); } },
      subscribe: (l) => { listeners.add(l); return () => listeners.delete(l); },
    };
  }
  return ref.current;
}

function useScrubValue(store) {
  const [v, setV] = useState(store.get());
  useEffect(() => store.subscribe(setV), [store]);
  return v;
}

const ScrubbedDiagram = ({ store, series, hybrid, modeLabels }) => {
  const index = useScrubValue(store);
  const [strategy, setStrategy] = useState(hybrid ? 'ecms' : 'ice');
  return <HevArchitectureDiagram series={series} index={index} onIndex={store.set} strategy={strategy} onStrategy={setStrategy} hybrid={hybrid} modeLabels={modeLabels} />;
};

const ScrubbedModeBand = ({ store, series, zoom, modeLabels }) => {
  const index = useScrubValue(store);
  return <HevModeBand series={series} zoom={zoom} scrubTime={series.time_s[index]} modeLabels={modeLabels} />;
};

function anchorFor(preset, presetModified, cycle) {
  const a = preset?.anchor;
  if (!a || presetModified) return null;
  if (cycle === a.cycle) {
    return {
      value: a.l_per_100km, label: a.label,
      explain: 'WLTP is a cold-start test with its own road-load and test-mass rules; this quasi-static model starts warm and uses the EPA coastdown, so it is expected to read low here. The hot-start US06 comparison (select US06) is the cleaner check.',
    };
  }
  if (cycle === 'us06' && a.hot_us06_l_per_100km) {
    return {
      value: a.hot_us06_l_per_100km, label: 'EPA unadjusted US06 dyno result, hot start (52.9 mpg)',
      explain: 'US06 is run hot, so this comparison is free of the cold-start effect the model excludes.',
    };
  }
  return null;
}

const HevEnergyManagementSimulator = () => {
  const initial = findPreset(DEFAULT_PRESET_ID);
  const [presetId, setPresetId] = useState(DEFAULT_PRESET_ID);
  const [vehicleRaw, setVehicleRaw] = useState(() => toRawStrings(initial.vehicle));
  const [gearboxRaw, setGearboxRaw] = useState(() => gearboxToRaw(initial.gearbox));
  const [engineRaw, setEngineRaw] = useState(() => toRawStrings(initial.engine));
  const [motorRaw, setMotorRaw] = useState(() => toRawStrings(initial.motor));
  const [batteryRaw, setBatteryRaw] = useState(() => toRawStrings(initial.battery));
  const [ruleRaw, setRuleRaw] = useState(() => toRawStrings(initial.rule || DEFAULT_RULE));
  const [ecmsRaw, setEcmsRaw] = useState(() => toRawStrings(DEFAULT_ECMS));
  const [driveCycle, setDriveCycle] = useState(initial.drive_cycle);
  const [sampling, setSampling] = useState('1');
  const [architecture, setArchitecture] = useState('p2');
  const preset = findPreset(presetId);

  const loadPreset = (id) => {
    const p = findPreset(id);
    if (!p) return;
    setPresetId(id);
    setVehicleRaw(toRawStrings(p.vehicle));
    setGearboxRaw(gearboxToRaw(p.gearbox));
    setEngineRaw(toRawStrings(p.engine));
    setMotorRaw(toRawStrings(p.motor));
    setBatteryRaw(toRawStrings(p.battery));
    setRuleRaw(toRawStrings(p.rule || DEFAULT_RULE));
    setDriveCycle(p.drive_cycle);
  };
  const presetModified = !!preset && JSON.stringify([vehicleRaw, gearboxRaw, engineRaw, motorRaw, batteryRaw, ruleRaw])
    !== JSON.stringify([toRawStrings(preset.vehicle), gearboxToRaw(preset.gearbox), toRawStrings(preset.engine),
      toRawStrings(preset.motor), toRawStrings(preset.battery), toRawStrings(preset.rule || DEFAULT_RULE)]);

  const setter = (fn) => (key, value) => fn((prev) => ({ ...prev, [key]: value }));
  const setVehicleField = setter(setVehicleRaw);
  const setGearboxField = setter(setGearboxRaw);
  const setEngineField = setter(setEngineRaw);
  const setMotorField = setter(setMotorRaw);
  const setBatteryField = setter(setBatteryRaw);
  const setRuleField = setter(setRuleRaw);
  const setEcmsField = setter(setEcmsRaw);

  const groups = useMemo(() => {
    const vehicle = validateGroup(vehicleRaw, VEHICLE_FIELD_SPECS);
    const gearbox = validateGroup(gearboxRaw, GEARBOX_FIELD_SPECS);
    const gears = parseGearRatios(gearboxRaw.gear_ratios);
    if (gears.error) gearbox.errors.gear_ratios = gears.error;
    else gearbox.values.gear_ratios = gears.values;
    const engine = validateGroup(engineRaw, ENGINE_FIELD_SPECS);
    engine.values.map_id = engineRaw.map_id;
    const motor = validateGroup(motorRaw, MOTOR_FIELD_SPECS);
    motor.values.drive_technology = motorRaw.drive_technology;
    const battery = validateGroup(batteryRaw, BATTERY_FIELD_SPECS);
    const rule = validateGroup(ruleRaw, RULE_FIELD_SPECS);
    const ecms = ecmsRaw.s_mode === 'manual' ? validateGroup(ecmsRaw, [ECMS_S_SPEC]) : { errors: {}, values: { s_manual: Number(ecmsRaw.s_manual) || 2.5 } };
    ecms.values.s_mode = ecmsRaw.s_mode;
    const cross = validateCrossFields({
      gearbox: gearbox.values, engine: engine.values, motor: motor.values, battery: battery.values, rule: rule.values,
    }, gears.values ? gears.values.length : 0);
    for (const [g, errs] of Object.entries(cross)) {
      const target = { gearbox, engine, motor, battery, rule }[g];
      for (const [k, msg] of Object.entries(errs)) if (!target.errors[k]) target.errors[k] = msg;
    }
    return { vehicle, gearbox, engine, motor, battery, rule, ecms };
  }, [vehicleRaw, gearboxRaw, engineRaw, motorRaw, batteryRaw, ruleRaw, ecmsRaw]);
  const hasErrors = Object.values(groups).some((g) => Object.keys(g.errors).length > 0);

  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState(null);
  const [result, setResult] = useState(null);
  const [resultMeta, setResultMeta] = useState(null);
  const snapshotRef = useRef(null);
  const snapshot = () => JSON.stringify({ vehicleRaw, gearboxRaw, engineRaw, motorRaw, batteryRaw, ruleRaw, ecmsRaw, driveCycle, sampling, architecture });
  const stale = !!result && snapshotRef.current !== null && snapshotRef.current !== snapshot();

  const runSimulation = async () => {
    setRunning(true);
    setRunError(null);
    try {
      const gb = { ...groups.gearbox.values };
      if (gb.final_drive_upper == null) delete gb.final_drive_upper;
      const response = await simulationApi.runHevEnergyManagement({
        architecture, drive_cycle: driveCycle, sampling_time_s: Number(sampling),
        vehicle: groups.vehicle.values, gearbox: gb, engine: groups.engine.values, motor: groups.motor.values,
        battery: groups.battery.values, rule: groups.rule.values, ecms: groups.ecms.values,
      });
      setResult(response);
      setResultMeta({ anchor: anchorFor(preset, presetModified, driveCycle) });
      snapshotRef.current = snapshot();
    } catch (e) {
      setRunError(e.message);
    }
    setRunning(false);
  };

  return (
    <div className="space-y-6">
      <Card className="technical-card">
        <CardHeader className="p-4 pb-2">
          <CardTitle className="font-chivo uppercase tracking-tight flex items-center gap-2 text-base">
            <Fuel className="h-5 w-5 text-autoxium-blue" />
            HEV Energy Management Simulator
          </CardTitle>
          <CardDescription className="text-xs">
            Pick a hybrid, drive cycle and strategy settings, then submit: the same car is driven by a
            rule-based controller, by ECMS and as a conventional (ICE-only) car, all charge-sustaining.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-4 pt-0 space-y-1">
          <div className="rounded-md border border-border/60 bg-muted/30 p-3 mb-2 space-y-2" data-testid="hev-preset-panel">
            <div className="flex flex-col sm:flex-row sm:items-end gap-2">
              <div className="space-y-1 flex-1 min-w-0">
                <Label className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground leading-tight flex items-center gap-1.5">
                  <CarFront className="h-3.5 w-3.5" /> Vehicle Preset
                </Label>
                <Select value={presetId} onValueChange={loadPreset}>
                  <SelectTrigger className="h-8 text-sm" data-testid="hev-preset-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {HEV_PRESETS.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {presetModified && (
                <Button variant="outline" size="sm" className="h-8 shrink-0" onClick={() => loadPreset(presetId)} data-testid="hev-preset-reset">
                  <RotateCcw className="h-3.5 w-3.5" /> Reset to preset
                </Button>
              )}
            </div>
            {preset && (
              <div className="text-xs text-muted-foreground space-y-1" data-testid="hev-preset-note">
                <p>
                  {preset.note}
                  {presetModified && <span className="ml-2 text-amber-600 dark:text-amber-400">(inputs modified)</span>}
                </p>
                <details>
                  <summary className="cursor-pointer select-none">Sources &amp; estimated values</summary>
                  <ul className="list-disc pl-5 mt-1 space-y-1">
                    {preset.sources.map((s) => (
                      <li key={s.item}>
                        {s.item}:{' '}
                        {s.refs.map((r, i) => (
                          <span key={r.url + i}>{i > 0 && '; '}<a href={r.url} target="_blank" rel="noopener noreferrer" className="underline break-words">{r.title}</a></span>
                        ))}
                      </li>
                    ))}
                    {preset.estimated?.length > 0 && <li>Estimated (not published): {preset.estimated.join('; ')}.</li>}
                  </ul>
                </details>
              </div>
            )}
          </div>

          <Accordion type="multiple" defaultValue={['cycle', 'vehicle', 'engine', 'motor', 'battery', 'gearbox', 'strategy']}>
            <Section value="cycle" icon={Route} title="Architecture & Drive Cycle">
              <div className={GRID}>
                <SelectField label="Architecture" value={architecture} onChange={setArchitecture} options={ARCHITECTURE_OPTIONS} testId="hev-architecture-select" />
                <SelectField label="Drive Cycle" value={driveCycle} onChange={setDriveCycle} options={DRIVE_CYCLE_OPTIONS} testId="hev-cycle-select" />
                <SelectField label="Sampling Time" value={sampling} onChange={setSampling} options={SAMPLING_OPTIONS} testId="hev-sampling-select" />
              </div>
              <p className="text-xs text-muted-foreground">Power-split (THS-style eCVT) is planned as a second phase.</p>
            </Section>

            <Section value="vehicle" icon={Car} title="Vehicle">
              <div className={GRID}>
                {VEHICLE_FIELD_SPECS.map((spec) => (
                  <FieldInput key={spec.key} spec={spec} raw={vehicleRaw[spec.key]} error={groups.vehicle.errors[spec.key]} onChange={setVehicleField} testPrefix="hev-vehicle" />
                ))}
              </div>
            </Section>

            <Section value="engine" icon={Cog} title="Engine">
              <div className={GRID}>
                <SelectField label="Fuel Map (US EPA)" value={engineRaw.map_id} onChange={(v) => setEngineField('map_id', v)} options={ENGINE_MAP_OPTIONS} testId="hev-map-select" />
                {ENGINE_FIELD_SPECS.map((spec) => (
                  <FieldInput key={spec.key} spec={spec} raw={engineRaw[spec.key]} error={groups.engine.errors[spec.key]} onChange={setEngineField} testPrefix="hev-engine" />
                ))}
              </div>
              <p className="text-xs text-muted-foreground">The 2.5 L map is scaled to your displacement (torque) and max power (speed); efficiency values are kept.</p>
            </Section>

            <Section value="motor" icon={Zap} title="Motor-Generator">
              <div className={GRID}>
                <SelectField label="Motor & Inverter Technology" value={motorRaw.drive_technology} onChange={(v) => setMotorField('drive_technology', v)} options={DRIVE_TECHNOLOGY_OPTIONS} testId="hev-tech-select" />
                {MOTOR_FIELD_SPECS.map((spec) => (
                  <FieldInput key={spec.key} spec={spec} raw={motorRaw[spec.key]} error={groups.motor.errors[spec.key]} onChange={setMotorField} testPrefix="hev-motor" />
                ))}
              </div>
            </Section>

            <Section value="battery" icon={BatteryCharging} title="Battery">
              <div className={GRID}>
                {BATTERY_FIELD_SPECS.map((spec) => (
                  <FieldInput key={spec.key} spec={spec} raw={batteryRaw[spec.key]} error={groups.battery.errors[spec.key]} onChange={setBatteryField} testPrefix="hev-battery" />
                ))}
              </div>
            </Section>

            <Section value="gearbox" icon={Settings2} title="Gearbox (P2)">
              <div className={GRID}>
                <FieldInput spec={GEAR_RATIO_SPEC} raw={gearboxRaw.gear_ratios} error={groups.gearbox.errors.gear_ratios} onChange={setGearboxField} testPrefix="hev-gearbox" type="text" />
                {GEARBOX_FIELD_SPECS.map((spec) => (
                  <FieldInput key={spec.key} spec={spec} raw={gearboxRaw[spec.key]} error={groups.gearbox.errors[spec.key]} onChange={setGearboxField} testPrefix="hev-gearbox"
                    placeholder={spec.key === 'final_drive_upper' ? 'blank = none' : undefined} />
                ))}
              </div>
              <p className="text-xs text-muted-foreground">Fixed shift schedule: the highest gear whose input speed is above the upshift speed, which rises from light to full load.</p>
            </Section>

            <Section value="strategy" icon={SlidersHorizontal} title="Strategy Parameters">
              <p className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Rule-based</p>
              <div className={GRID}>
                {RULE_FIELD_SPECS.map((spec) => (
                  <FieldInput key={spec.key} spec={spec} raw={ruleRaw[spec.key]} error={groups.rule.errors[spec.key]} onChange={setRuleField} testPrefix="hev-rule" />
                ))}
              </div>
              <p className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground pt-2">ECMS</p>
              <div className={GRID}>
                <SelectField label="Equivalence Factor" value={ecmsRaw.s_mode} onChange={(v) => setEcmsField('s_mode', v)} options={ECMS_MODE_OPTIONS} testId="hev-ecms-mode-select" />
                {ecmsRaw.s_mode === 'manual' && (
                  <FieldInput spec={ECMS_S_SPEC} raw={ecmsRaw.s_manual} error={groups.ecms.errors.s_manual} onChange={setEcmsField} testPrefix="hev-ecms" />
                )}
              </div>
            </Section>
          </Accordion>

          <div className="pt-3">
            <Button onClick={runSimulation} disabled={hasErrors || running} className="w-full" data-testid="run-hev-simulation-button">
              {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
              Submit &amp; Run Simulation
            </Button>
            {hasErrors && <p className="text-xs text-destructive mt-2" data-testid="hev-input-errors">Fix the highlighted inputs to run.</p>}
            {runError && (
              <div className="flex items-center gap-2 text-sm text-destructive mt-3" data-testid="hev-run-error">
                <AlertCircle className="h-4 w-4 shrink-0" /> {runError}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {result ? (
        <HevResults result={result} stale={stale} anchor={resultMeta?.anchor} />
      ) : (
        <Card className="technical-card">
          <CardContent className="py-10 text-center text-sm text-muted-foreground" data-testid="hev-empty-state">
            Fill in the inputs above and submit to see results and charts.
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const HevResults = React.memo(function HevResults({ result, stale, anchor }) {
  const rows = useTraceRows(result.series);
  const zoom = useChartZoom();
  const store = useScrubStore();
  const timeIndex = useMemo(() => new Map(result.series.time_s.map((t, i) => [t, i])), [result]);
  const onHover = useCallback((t) => {
    const i = timeIndex.get(t);
    if (i !== undefined) store.set(i);
  }, [timeIndex, store]);
  // Memoise the zoom object on its state so the memoised trace panels skip unrelated renders.
  // (xDomain is a fresh array each render, so key on its value.)
  const domainKey = JSON.stringify(zoom.xDomain);
  const zoomMemo = useMemo(() => zoom, [domainKey, zoom.isSelecting, zoom.refAreaLeft, zoom.refAreaRight]); // eslint-disable-line react-hooks/exhaustive-deps
  const hybrid = result.components.hybrid;
  const modeLabels = result.method.mode_labels;
  const hasS = !!result.series.strategies.ecms.equivalence_factor;

  return (
    <div className={`space-y-6 transition-opacity ${stale ? 'opacity-50 pointer-events-none' : ''}`} data-testid="hev-results">
      {stale && (
        <Alert data-testid="hev-stale-banner" className="pointer-events-auto">
          <RotateCcw className="h-4 w-4" />
          <AlertDescription>Inputs have changed — submit again to update these results.</AlertDescription>
        </Alert>
      )}
      {result.warnings?.length > 0 && (
        <Alert data-testid="hev-warnings" className="border-amber-500/50">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          <AlertDescription>
            <p className="font-semibold mb-1">The simulation ran, but check these points:</p>
            <ul className="list-disc pl-5 space-y-1 text-sm">
              {result.warnings.map((w) => <li key={w}>{w}</li>)}
            </ul>
          </AlertDescription>
        </Alert>
      )}
      <HevSummaryPanel summary={result.summary} anchor={anchor} cycle={result.cycle} />
      <ScrubbedDiagram key={`diagram-${result.compute_time_ms}`} store={store} series={result.series} hybrid={hybrid} modeLabels={modeLabels} />
      <HevTimeTraces rows={rows} zoom={zoomMemo} onHover={onHover} components={result.components} hybrid={hybrid} hasS={hasS} />
      <ScrubbedModeBand store={store} series={result.series} zoom={zoomMemo} modeLabels={modeLabels} />
      <Suspense fallback={<Card className="technical-card"><CardContent className="py-10 text-center text-sm text-muted-foreground">Loading efficiency map…</CardContent></Card>}>
        <HevBsfcMapChart engineMap={result.engine_map} operatingPoints={result.operating_points} hybrid={hybrid} />
      </Suspense>
      <HevEnergyBreakdown energy={result.energy} summary={result.summary} />
      <p className="text-xs text-muted-foreground" data-testid="hev-method-note">
        Fuel: {result.method.fuel.source}; CO₂ {result.method.fuel.co2_kg_per_l.toFixed(3)} kg/L. {result.method.charge_sustaining}{' '}
        Engine start penalty {result.method.start_fuel_g} g per start. Computed in {(result.compute_time_ms / 1000).toFixed(2)} s.
      </p>
    </div>
  );
});

export default HevEnergyManagementSimulator;
